import React, {
  ChangeEvent,
  KeyboardEvent,
  useCallback,
  useMemo,
  useState,
} from 'react'

import { ArrowButton } from 'components/button/ButtonWithIcon'
import { Form } from 'components/form-elements/Form'
import { TextInput } from 'components/form-elements/TextInput'

import { useInnskraningSentryLogging } from 'hooks/onboarding/useInnskraningExceptionCapture'
import { useInnskraningOnMount } from 'hooks/onboarding/useInnskraningOnMount'
import { useOnMount } from 'hooks/useOnMount'
import { Properties } from 'types/onboarding'
import {
  InnskraningAnalyticsStepEnum,
  trackInnskraningEvent,
} from 'utils/onboarding/analytics'

import Spinner from '../spinner/Spinner'
import { StepHeader } from '../step-header/StepHeader'

import { AddressPickerResults } from './AddressPickerResults'

import s from './AddressPicker.module.scss'
import { scrollToTopOfPage } from 'utils/scrollToTopOfPage'
import { Button } from 'components/button/Button'
import { ErrorMessage } from 'onboarding/components/errormessage/ErrorMessage'

type PropertyOfOtherKind = {
  selected: boolean
  propertyNumber: string
  apartmentNumber: string
  size: string | number
}

type SearchResults = Array<{
  address: string
  postalCode: string
  neighborhood: string
  properties: Array<PropertyOfOtherKind>
}>

const htmlClass = (className: string, add: boolean) => {
  document.documentElement.classList[add ? 'add' : 'remove'](className)
}

type Props = {
  onSubmit: () => void
  properties: Properties
  setProperties: (properties: Properties) => void
  onIsLoading: (isLoading: boolean) => void
}

export const AddressPicker = (props: Props) => {
  const { onSubmit, properties, setProperties, onIsLoading } = props

  const { captureException } = useInnskraningSentryLogging()
  // TODO use hook here
  const [state, _setState] = useState({
    loading: false,
    searchQuery: '',
    searchResults: [] as SearchResults,
    noResults: false,
    error: false,
    errorMessage: '',
  })

  const isAnyPropertySelected = useMemo(
    () =>
      properties.some((p) => p.selected) ||
      state.searchResults.some((r) => r.properties.some((p) => p.selected)),
    [properties, state.searchResults]
  )

  const setState = useCallback((newState: Partial<typeof state>) => {
    _setState((prevState) => ({
      ...prevState,
      ...newState,
    }))
  }, [])

  useInnskraningOnMount(() => {
    //TODO: remove this stuff
    htmlClass('loggedIn', true)
  })

  const search = () => {
    trackInnskraningEvent(
      InnskraningAnalyticsStepEnum.ADD_ADDRESS_SEARCH_BUTTON_CLICK
    )

    const query = state.searchQuery

    if (!query.length) {
      setState({
        error: true,
        errorMessage: 'Vinsamlegast sláðu inn heimilisfang',
      })
      return
    } else if (state.error) {
      setState({
        error: false,
        errorMessage: '',
      })
    }

    setState({
      noResults: false,
      searchResults: [],
      loading: true,
    })

    onIsLoading(true)

    fetch(`/api/onboarding/property-search?address=${query}`, {
      method: 'GET',
    })
      .then((res) => {
        if (res.status >= 400) {
          console.error(res)
          throw new Error()
        }
        return res.json()
      })
      .then((res) => {
        setState({
          searchResults: res,
          error: false,
          errorMessage: '',
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          noResults: res.length === 0,
        })
      })
      .catch((e) => {
        captureException(e, () => ({
          tags: {
            component: AddressPicker.name,
          },
          contexts: {
            error: {
              message:
                'Eitthvað mistókst í leitinni, vinsamlegast reynið aftur',
            },
          },
        }))

        setState({
          error: true,
          errorMessage:
            'Eitthvað mistókst í leitinni. Vinsamlegast reynið aftur og sláið inn götuheiti ásamt húsnúmeri.',
        })
      })
      .finally(() => {
        setState({ loading: false })
        onIsLoading(false)
      })
  }

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setState({
        [e.target.name]: e.target.value,
      })
    },
    [setState]
  )

  const onKeyUp = (e: KeyboardEvent<HTMLInputElement>) => {
    // If enter, trigger search
    //TODO: fix
    if (e.keyCode === 13) {
      search()
    }
  }

  const toggleProperty = (resultsIndex: number, propertyIndex: number) => {
    const previousToggleState =
      state.searchResults[resultsIndex].properties[propertyIndex].selected

    const newResults = [...state.searchResults]
    newResults[resultsIndex].properties[propertyIndex].selected =
      !previousToggleState

    setState({ searchResults: newResults })
  }

  const onSubmitClick = () => {
    trackInnskraningEvent(
      InnskraningAnalyticsStepEnum.ADD_ADDRESS_ADD_BUTTON_CLICK
    )

    const currentAddresses = [...properties]

    const flatPropertyNumbers = currentAddresses.flatMap((a) => a.fNumber)

    state.searchResults.forEach((result) => {
      result.properties.forEach((property) => {
        if (
          property.selected &&
          !flatPropertyNumbers.includes(property.propertyNumber)
        ) {
          currentAddresses.push({
            fNumber: property.propertyNumber,
            address: result.address,
            postalCode: result.postalCode,
            city: result.neighborhood,
            selected: true,
          })
        }
      })
    })

    setProperties(currentAddresses)

    onSubmit()
  }

  useOnMount(() => {
    scrollToTopOfPage()

    trackInnskraningEvent(InnskraningAnalyticsStepEnum.ADD_ADDRESS_PAGELOAD)
  })

  return (
    <div className={s.addressPicker}>
      <StepHeader
        title="Bæta við heimilisfangi"
        text="Hér getur þú leitað eftir götuheiti og húsnúmeri. Þú getur líka notast við
        fasteignanúmer sem hentar vel fyrir bústaði, eignir á byggingarstigi og annað
        slíkt."
      />

      <Form
        onSubmit={(e) => {
          e.preventDefault()
          search()
        }}
        omitSubmitButton={true}
      >
        <div>
          <div className={s.addressPicker__container}>
            <div className={s.addressPicker__textInput}>
              <TextInput
                icon="house"
                required
                name="searchQuery"
                value={state.searchQuery}
                onChange={onChange}
                onKeyUp={onKeyUp}
                placeholder="Sláðu inn götuheiti og húsnúmer"
                disabled={state.loading}
                autoFocus
              />
            </div>
            <div>
              <Button onClick={search} disabled={state.loading}>
                Leita
              </Button>
            </div>
          </div>
        </div>

        {state.loading && <Spinner centered text="Sæki leitarniðurstöður" />}

        {state.searchResults.length > 0 && (
          <AddressPickerResults
            results={state.searchResults}
            toggleProperty={toggleProperty}
          />
        )}
        {state.error && <ErrorMessage message={state.errorMessage} />}

        {state.noResults && !state.error && (
          <ErrorMessage
            message="Engar niðurstöður fundust. Vinsamlegast reynið aftur og sláið inn
            götuheiti ásamt húsnúmeri."
          />
        )}

        {state.searchResults.length > 0 && (
          <ArrowButton
            type="button"
            disabled={!isAnyPropertySelected}
            onClick={onSubmitClick}
          >
            Velja og bæta við
          </ArrowButton>
        )}
      </Form>
    </div>
  )
}
